// Tab "Grupos" do EditUserModal (PO1-2024).
//
// Carrega lista de roles disponíveis (de /roles) + roles atuais do user
// (de /users/:id/groups). Multi-select com checkboxes. Salvar dispara PUT
// que reconcilia via diff e invalida cache do user no backend.

import React, { useEffect, useState, useCallback } from "react";
import { Button, FormGroup, Label, Input } from "reactstrap";

import { listRoles } from "services/rolesApi";
import { getUserGroups, setUserGroups } from "services/usersApi";
import { useToast } from "hooks/toast";
import { useAuth } from "hooks/auth";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";

export default function UserGroupsTab({ user, onCountChange, onSaved }) {
  // PO1-3296: o nome técnico do grupo (Name do ABP, às vezes um hash) só
  // interessa ao admin Everlog; para o cliente fica só o nome de exibição.
  const { user: logado } = useAuth();
  const isAdmin = !!(logado && logado.setor === "admin");
  const [allRoles, setAllRoles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [originalSelected, setOriginalSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const { addToast } = useToast();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([listRoles(), getUserGroups(user.id)])
      .then(([rolesData, userGroupsData]) => {
        if (cancelled) return;
        setAllRoles(rolesData.items || []);
        const currentIds = new Set(
          (userGroupsData.items || []).map((r) => r.Id)
        );
        setSelected(currentIds);
        setOriginalSelected(currentIds);
        if (onCountChange) onCountChange(currentIds.size);
      })
      .catch((err) => {
        console.error("[UserGroupsTab] load:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Não foi possível carregar grupos",
          icon: "tim-icons icon-alert-circle-exc",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, addToast, onCountChange]);

  const handleToggle = useCallback(
    (roleId) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(roleId)) next.delete(roleId);
        else next.add(roleId);
        if (onCountChange) onCountChange(next.size);
        return next;
      });
    },
    [onCountChange]
  );

  const isDirty =
    selected.size !== originalSelected.size ||
    Array.from(selected).some((id) => !originalSelected.has(id));

  const handleSave = useCallback(async () => {
    if (!user) return;
    setSaving(true);
    try {
      const result = await setUserGroups(user.id, Array.from(selected));
      addToast({
        place: "tr",
        type: "success",
        title: "Salvo",
        description: `${result.added.length} adicionado(s), ${result.removed.length} removido(s).`,
        icon: "tim-icons icon-check-2",
      });
      setOriginalSelected(new Set(selected));
      if (onSaved) onSaved(result);
    } catch (err) {
      console.error("[UserGroupsTab] save:", err);
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        "Erro ao salvar grupos";
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [user, selected, addToast, onSaved]);

  const filteredRoles = filter
    ? allRoles.filter((r) => {
        const f = filter.toLowerCase();
        return (
          (r.Name || "").toLowerCase().includes(f) ||
          (r.DisplayName || "").toLowerCase().includes(f)
        );
      })
    : allRoles;

  if (loading) {
    return (
      <div style={{ color: AUTOPILOT_COLORS.muted, textAlign: "center", padding: 20 }}>
        Carregando grupos…
      </div>
    );
  }

  return (
    <div>
      <FormGroup>
        <Input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrar grupos…"
          style={{ marginBottom: 12 }}
        />
      </FormGroup>

      <div
        style={{
          maxHeight: 300,
          overflowY: "auto",
          border: `1px solid ${AUTOPILOT_COLORS.border}`,
          borderRadius: 4,
          padding: 8,
        }}
      >
        {filteredRoles.length === 0 ? (
          <div style={{ color: AUTOPILOT_COLORS.muted, padding: 12, textAlign: "center" }}>
            {filter ? "Nenhum grupo bate com o filtro" : "Nenhum grupo cadastrado"}
          </div>
        ) : (
          filteredRoles.map((role) => (
            <Label
              key={role.Id}
              check
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 4px",
                borderBottom: `1px solid ${AUTOPILOT_COLORS.divider}`,
                cursor: "pointer",
                color: AUTOPILOT_COLORS.title,
                margin: 0,
              }}
            >
              <Input
                type="checkbox"
                checked={selected.has(role.Id)}
                onChange={() => handleToggle(role.Id)}
              />
              <span style={{ fontWeight: 600 }}>
                {role.DisplayName || role.Name}
              </span>
              {isAdmin && role.DisplayName && role.Name !== role.DisplayName && (
                <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: 11 }}>
                  ({role.Name})
                </span>
              )}
              {role.userCount !== undefined && (
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 11,
                    color: AUTOPILOT_COLORS.muted,
                  }}
                >
                  {role.userCount} {role.userCount === 1 ? "usuário" : "usuários"}
                </span>
              )}
            </Label>
          ))
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 16,
        }}
      >
        <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: 12 }}>
          {selected.size} {selected.size === 1 ? "grupo" : "grupos"} selecionado
          {selected.size === 1 ? "" : "s"}
        </div>
        <Button
          color="success"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving ? "Salvando…" : "Salvar grupos"}
        </Button>
      </div>
    </div>
  );
}
